import { computed, Injectable, signal } from '@angular/core';
import { EcommerceSessionModel } from '../../models/ecommerce/auth.model';

const SESSION_STORAGE_KEY = 'nfarm_ecommerce_session';

/**
 * Store della sessione ecommerce (token opaco del modulo arpes_api).
 * Distinto sia dal token Keycloak sia dal cart_token, come da contratto API.
 */
@Injectable({
    providedIn: 'root',
})
export class EcommerceSessionService {
    private readonly sessionSignal = signal<EcommerceSessionModel | null>(this.readFromStorage());

    readonly session = computed(() => this.sessionSignal());
    readonly isAuthenticated = computed(() => this.isValid(this.sessionSignal()));

    /** Token valido da allegare come Bearer, o null se assente/scaduto. */
    get accessToken(): string | null {
        const session = this.sessionSignal();
        return this.isValid(session) ? session!.access_token : null;
    }

    setSession(session: EcommerceSessionModel): void {
        this.sessionSignal.set(session);
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }

    /** Da chiamare su logout e su 401: elimina la sessione senza toccare il carrello. */
    clearSession(): void {
        this.sessionSignal.set(null);
        localStorage.removeItem(SESSION_STORAGE_KEY);
    }

    private isValid(session: EcommerceSessionModel | null): boolean {
        if (!session?.access_token || !session.expires_at) {
            return false;
        }
        return new Date(session.expires_at).getTime() > Date.now();
    }

    private readFromStorage(): EcommerceSessionModel | null {
        try {
            const raw = localStorage.getItem(SESSION_STORAGE_KEY);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as EcommerceSessionModel;
            return this.isValid(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
}
